import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaPrint, FaFileAlt } from 'react-icons/fa';

// ─── Helpers ──────────────────────────────────────────────────

const fmt = (n, decimals = 0) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: decimals });

const pct = (n) =>
  n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`;

const fmtDuration = (seconds) => {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const dim = (row, i = 0) => row?.dimensionValues?.[i]?.value ?? '';
const met = (row, i = 0) => Number(row?.metricValues?.[i]?.value ?? 0);

const NAVY   = '#1e3a5f';
const YELLOW = '#cddd3a';

const CHANNEL_COLORS = {
  'Organic Search': NAVY,
  'Direct':         YELLOW,
  'Referral':       '#6366f1',
  'Organic Social': '#ec4899',
  'Paid Search':    '#f97316',
  'Email':          '#14b8a6',
  'Unassigned':     '#9ca3af',
};

// ─── Static rank-tracker data (Rank Tracker v8.51, May 13 2026) ──

const RANK_DATA = {
  date: 'May 13, 2026',
  keywordsTracked: 9,
  searchEngines: 2,
  visibility: { overall: 16, google: 11, yahoo: 20 },
  keywords: [
    { kw: 'Jamaica jobs',      google: '>30', yahoo: '>30', priority: 'high' },
    { kw: 'job search',        google: '>30', yahoo: '>30', priority: 'high' },
    { kw: 'job portal',        google: '>30', yahoo: '>30', priority: 'high' },
    { kw: 'employment Jamaica', google: 30,   yahoo: 7,    priority: 'medium' },
    { kw: 'careers Jamaica',   google: '>30', yahoo: '>30', priority: 'medium' },
    { kw: 'hiring',            google: '>30', yahoo: '>30', priority: 'medium' },
    { kw: 'recruitment',       google: 1,     yahoo: '>30', priority: 'low' },
    { kw: 'hire me',           google: 94,    yahoo: 1,    priority: 'low' },
  ],
};

const COMPETITORS = [
  { name: 'LinkedIn',             domain: 'linkedin.com',             type: 'Major Platform' },
  { name: 'Indeed',               domain: 'indeed.com',               type: 'Major Platform' },
  { name: 'Glassdoor',            domain: 'glassdoor.com',            type: 'Major Platform' },
  { name: 'ZipRecruiter',         domain: 'ziprecruiter.com',         type: 'Major Platform' },
  { name: 'Jooble',               domain: 'jooble.org',               type: 'Major Platform' },
  { name: 'CaribbeanJobs',        domain: 'caribbeanjobs.com',        type: 'Caribbean Board' },
  { name: 'CaribbeanJobsOnline',  domain: 'caribbeanjobsonline.com',  type: 'Caribbean Board' },
  { name: 'Jobs Jamaica (Gov)',    domain: 'jamaicajobs.gov.jm',       type: 'Government' },
  { name: 'JCA',                  domain: 'jca.gov.jm',               type: 'Government' },
  { name: 'MOH',                  domain: 'moh.gov.jm',               type: 'Government' },
  { name: 'OSC',                  domain: 'osc.gov.jm',               type: 'Government' },
  { name: 'MOEY',                 domain: 'jobs.moey.gov.jm',         type: 'Government' },
  { name: 'MLSS',                 domain: 'mlss.gov.jm',              type: 'Government' },
  { name: 'HEART/NSTA',           domain: 'jobs.heart-nsta.org',      type: 'Government' },
  { name: 'LMIS',                 domain: 'lmis.gov.jm',              type: 'Government' },
  { name: 'PICA',                 domain: 'pica.gov.jm',              type: 'Government' },
  { name: 'Tax Administration',   domain: 'jamaicatax.gov.jm',        type: 'Government' },
  { name: 'JIS',                  domain: 'jis.gov.jm',               type: 'Government' },
  { name: 'UN Jobs',              domain: 'unjobs.org',               type: 'Government' },
  { name: 'BigUpJA',              domain: 'bigupja.com',              type: 'Local' },
  { name: 'Jobs Jamaica',         domain: 'jobsjamaica.net',          type: 'Local' },
  { name: 'Jamaica Classified',   domain: 'jamaicaclassifiedonline.com', type: 'Local' },
  { name: 'Jamaica Index',        domain: 'jamaicaindex.com',         type: 'Local' },
  { name: 'JobBox JA',            domain: 'jobboxja.zohorecruit.com', type: 'Local' },
  { name: 'Fiwi Business',        domain: 'fiwibusiness.com',         type: 'Local' },
  { name: 'My Island Jamaica',    domain: 'my-island-jamaica.com',    type: 'Local' },
  { name: 'Outscal',              domain: 'outscal.com',              type: 'International' },
  { name: 'Facebook — JM Jobs',   domain: 'facebook.com/groups',      type: 'Social' },
];

const COMP_STYLE = {
  'Major Platform': { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  'Caribbean Board': { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  'Government':      { bg: '#fefce8', text: '#a16207', border: '#fde68a' },
  'Local':           { bg: '#faf5ff', text: '#7c3aed', border: '#ddd6fe' },
  'International':   { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
  'Social':          { bg: '#fdf2f8', text: '#be185d', border: '#fbcfe8' },
};

// ─── Print styles ─────────────────────────────────────────────

const PRINT_STYLES = `
  @media print {
    body.seo-report-open > *:not(#seo-report-portal) {
      display: none !important;
    }
    #seo-report-portal {
      position: static !important;
      overflow: visible !important;
      background: white !important;
    }
    #seo-report-toolbar { display: none !important; }
    #seo-report-body    { box-shadow: none !important; max-width: 100% !important; }
    .page-break         { page-break-before: always; break-before: page; }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

// ─── Sub-components ───────────────────────────────────────────

const SectionTitle = ({ number, title }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="text-xs font-black tracking-widest" style={{ color: 'rgba(30,58,95,0.22)' }}>
      {number}
    </span>
    <h2 className="text-base font-bold whitespace-nowrap" style={{ color: NAVY }}>{title}</h2>
    <div className="flex-1 h-px" style={{ background: '#e5e7eb' }} />
  </div>
);

const Blurb = ({ children }) => (
  <p className="text-sm text-gray-500 leading-relaxed mb-5">{children}</p>
);

const KpiHero = ({ label, value, sub, accent, note }) => (
  <div className="rounded-xl border p-5" style={
    accent
      ? { background: NAVY, borderColor: NAVY }
      : { background: '#f9fafb', borderColor: '#e5e7eb' }
  }>
    <p className="text-xs font-semibold uppercase tracking-widest mb-1"
      style={{ color: accent ? '#93c5fd' : '#9ca3af' }}>
      {label}
    </p>
    <p className="text-3xl font-black" style={{ color: accent ? YELLOW : NAVY }}>
      {value}
    </p>
    {sub  && <p className="text-xs mt-1" style={{ color: accent ? '#bfdbfe' : '#9ca3af' }}>{sub}</p>}
    {note && <p className="text-xs mt-2 font-semibold" style={{ color: accent ? YELLOW : '#16a34a' }}>{note}</p>}
  </div>
);

const StatBox = ({ label, value, sub }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
    <p className="text-xl font-bold mt-0.5" style={{ color: NAVY }}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const Insight = ({ color = '#1d4ed8', bg = '#eff6ff', children }) => (
  <div className="rounded-lg px-4 py-3 text-sm leading-relaxed mt-4"
    style={{ background: bg, borderLeft: `4px solid ${color}`, color: '#374151' }}>
    {children}
  </div>
);

// KPI progress row: current value vs target
const KpiRow = ({ label, current, target, format, lowerIsBetter, how, timeline }) => {
  const cur = parseFloat(current) || 0;
  const tgt = parseFloat(target)  || 0;
  const progress = lowerIsBetter
    ? tgt > 0 ? Math.min(100, Math.round((tgt / Math.max(cur, tgt)) * 100)) : 0
    : tgt > 0 ? Math.min(100, Math.round((cur / tgt) * 100)) : 0;

  return (
    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
      <td className="py-3 px-3 text-sm font-semibold text-gray-700 w-44">{label}</td>
      <td className="py-3 px-3 text-sm font-bold text-center" style={{ color: NAVY }}>
        {format ? format(current) : current}
      </td>
      <td className="py-3 px-3 text-sm font-bold text-center text-green-700">
        {format ? format(target) : target}
      </td>
      <td className="py-3 px-3 w-48">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full" style={{ background: '#e5e7eb' }}>
            <div className="h-2 rounded-full" style={{ width: `${progress}%`, background: progress >= 100 ? '#16a34a' : NAVY }} />
          </div>
          <span className="text-xs text-gray-400 w-8 shrink-0">{progress}%</span>
        </div>
      </td>
      <td className="py-3 px-3 text-xs text-gray-400 w-20 text-center">{timeline}</td>
      <td className="py-3 px-3 text-xs text-gray-500">{how}</td>
    </tr>
  );
};

// Thin horizontal bar
const Bar = ({ value, max, color, height = 6 }) => (
  <div className="rounded-full" style={{ background: '#f3f4f6', height }}>
    <div className="rounded-full" style={{
      width: `${Math.max(2, Math.round((value / (max || 1)) * 100))}%`,
      background: color || NAVY,
      height,
    }} />
  </div>
);

// Rank-tracker position badge (handles numeric and ">30" strings)
const RankBadge = ({ pos, engine }) => {
  if (pos === '>30' || pos == null) {
    return <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#f3f4f6', color: '#9ca3af' }}>&gt;30</span>;
  }
  const p = parseInt(pos, 10);
  const [bg, text] =
    p <= 3   ? ['#dcfce7', '#15803d'] :
    p <= 10  ? ['#fef9c3', '#a16207'] :
    p <= 30  ? ['#fee2e2', '#b91c1c'] :
               ['#f3f4f6', '#6b7280'];
  return <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: bg, color: text }}>#{p}</span>;
};

// Position badge
const PosBadge = ({ pos }) => {
  const p = parseFloat(pos);
  if (!p) return <span className="text-gray-400 text-xs">—</span>;
  const [bg, text] = p <= 3
    ? ['#dcfce7', '#15803d']
    : p <= 10 ? ['#fef9c3', '#a16207']
    : p <= 20 ? ['#fee2e2', '#b91c1c']
    : ['#f3f4f6', '#6b7280'];
  return (
    <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: bg, color: text }}>
      #{p.toFixed(1)}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────

const SeoReport = ({
  days, overview, channels, pages, countries, devices,
  timeseries, gscQueries, moz, onClose,
}) => {
  useEffect(() => {
    document.body.classList.add('seo-report-open');
    const style = document.createElement('style');
    style.id = 'seo-report-print-styles';
    style.textContent = PRINT_STYLES;
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove('seo-report-open');
      document.getElementById('seo-report-print-styles')?.remove();
    };
  }, []);

  // ── Dates ────────────────────────────────────────────────────
  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const endDate   = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  const periodLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  // ── GA4 metrics ──────────────────────────────────────────────
  const ovRow          = overview?.rows?.[0] ?? {};
  const totalSessions  = met(ovRow, 0);
  const totalUsers     = met(ovRow, 1);
  const totalPageviews = met(ovRow, 2);
  const engagementRate = met(ovRow, 3);
  const avgDuration    = met(ovRow, 4);
  const newUsers       = met(ovRow, 5);

  const organicRow      = (channels || []).find(r => dim(r, 0) === 'Organic Search');
  const organicSessions = organicRow ? met(organicRow, 0) : 0;
  const organicPct      = totalSessions > 0
    ? ((organicSessions / totalSessions) * 100).toFixed(0)
    : 0;

  const tsData      = (timeseries || []).map(r => ({ date: dim(r, 0), sessions: met(r, 0) }));
  const maxSessions = Math.max(...tsData.map(d => d.sessions), 1);

  // ── GSC ──────────────────────────────────────────────────────
  const topKeywords    = (gscQueries || []).slice(0, 10);
  const avgPositionRaw = topKeywords.length > 0
    ? topKeywords.reduce((s, r) => s + (r.position || 0), 0) / topKeywords.length
    : null;
  const avgPosition    = avgPositionRaw ? avgPositionRaw.toFixed(1) : null;

  const totalImpressions = (gscQueries || []).reduce((s, r) => s + (r.impressions || 0), 0);
  const totalClicks      = (gscQueries || []).reduce((s, r) => s + (r.clicks || 0), 0);

  // Position distribution buckets
  const posBuckets = { top3: 0, page1: 0, page2: 0, beyond: 0 };
  (gscQueries || []).forEach(r => {
    const p = r.position || 99;
    if (p <= 3)       posBuckets.top3++;
    else if (p <= 10) posBuckets.page1++;
    else if (p <= 20) posBuckets.page2++;
    else              posBuckets.beyond++;
  });
  const totalKws = Object.values(posBuckets).reduce((a, b) => a + b, 0) || 1;

  // Opportunity keywords: high impressions, low CTR, positions 4-25
  const opportunityKws = (gscQueries || [])
    .filter(r => r.impressions >= 30 && (r.ctr || 0) < 0.03 && r.position > 3 && r.position <= 25)
    .slice(0, 5);

  // ── Moz ──────────────────────────────────────────────────────
  const mozSorted = moz?.sites
    ? [...moz.sites].sort((a, b) => (b.domain_authority || 0) - (a.domain_authority || 0))
    : [];
  const ourMoz = moz?.sites?.find(s => s.domain === 'hiremeja.com') || {};
  const maxDA  = mozSorted[0]?.domain_authority || 1;

  // ── LLM Visibility score (0–100) ─────────────────────────────
  const llmChecks = [
    {
      label: 'llms.txt file published',
      done: true,
      pts: 25,
      note: 'Tells AI crawlers exactly what your site covers — a direct ranking signal for LLM citations.',
    },
    {
      label: 'FAQ Schema markup on content pages',
      done: true,
      pts: 20,
      note: 'Structured Q&A is the format AI models prefer to cite as authoritative answers.',
    },
    {
      label: 'Dedicated career resource pages (salary, CV, industry)',
      done: true,
      pts: 15,
      note: 'Topical depth signals expertise. AI models cite sources that comprehensively cover a topic.',
    },
    {
      label: `Domain Authority ≥ 20 (currently ${ourMoz.domain_authority ?? '—'})`,
      done: (ourMoz.domain_authority || 0) >= 20,
      pts: 15,
      note: 'Higher DA correlates with higher citation probability in AI-generated answers.',
    },
    {
      label: 'Mobile-friendly & fast load',
      done: true,
      pts: 15,
      note: 'AI crawlers (and Google) prioritise sites that load quickly on mobile.',
    },
    {
      label: `Avg. SERP position ≤ 15 for target keywords (currently #${avgPosition ?? '—'})`,
      done: avgPositionRaw != null && avgPositionRaw <= 15,
      pts: 10,
      note: 'AI models primarily cite pages that already rank on page 1–2 of Google.',
    },
  ];
  const llmScore = llmChecks.filter(c => c.done).reduce((s, c) => s + c.pts, 0);
  const llmLabel = llmScore >= 80 ? 'Strong' : llmScore >= 60 ? 'Good' : llmScore >= 40 ? 'Moderate' : 'Needs Work';
  const llmColor = llmScore >= 80 ? '#16a34a' : llmScore >= 60 ? '#ca8a04' : '#dc2626';

  // ── KPI targets ──────────────────────────────────────────────
  const tgtOrganic    = Math.round(organicSessions * 1.25);
  const tgtSessions   = Math.round(totalSessions   * 1.20);
  const tgtDA         = (ourMoz.domain_authority || 0) + 4;
  const tgtPosition   = avgPositionRaw ? Math.max(1, avgPositionRaw * 0.78).toFixed(1) : null;
  const tgtEngagement = Math.min(0.95, (engagementRate || 0) + 0.08);

  return createPortal(
    <div id="seo-report-portal" className="fixed inset-0 z-[9999] overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.65)' }}>

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div id="seo-report-toolbar" className="sticky top-0 z-10 flex items-center justify-between px-6 py-3"
        style={{ background: NAVY }}>
        <div className="flex items-center gap-2">
          <FaFileAlt size={13} className="text-white" />
          <span className="font-semibold text-sm text-white">SEO Performance Report</span>
          <span className="text-xs" style={{ color: '#93c5fd' }}>
            · hiremeja.com · {days}-day period
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-sm font-bold px-4 py-1.5 rounded-md transition-opacity hover:opacity-90"
            style={{ background: YELLOW, color: NAVY }}
          >
            <FaPrint size={12} /> Print / Save PDF
          </button>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <FaTimes size={18} />
          </button>
        </div>
      </div>

      {/* ── Report body ──────────────────────────────────────── */}
      <div id="seo-report-body" className="max-w-4xl mx-auto bg-white shadow-2xl"
        style={{ minHeight: '100vh' }}>

        {/* ══ COVER ══════════════════════════════════════════ */}
        <div className="px-12 pt-14 pb-12" style={{ background: NAVY, minHeight: 300 }}>
          <div className="flex items-center gap-3 mb-14">
            <div className="font-black text-lg px-3 py-1 rounded"
              style={{ background: YELLOW, color: NAVY }}>HireMe</div>
            <span className="font-medium" style={{ color: '#93c5fd' }}>JA</span>
            <span className="ml-auto text-sm" style={{ color: '#60a5fa' }}>hiremeja.com</span>
          </div>

          <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: YELLOW }}>
            SEO Performance Report
          </p>
          <h1 className="text-4xl font-black text-white leading-tight">
            Search Engine Optimisation
          </h1>
          <h2 className="text-2xl font-light mt-1" style={{ color: '#bfdbfe' }}>
            Performance Overview
          </h2>

          <div className="mt-10 pt-6 grid grid-cols-4 gap-6"
            style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            {[
              { label: 'Reporting Period', value: periodLabel },
              { label: 'Report Generated', value: reportDate },
              { label: 'Domain', value: 'hiremeja.com' },
              { label: 'Data Sources', value: 'GA4 · GSC · Moz' },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: '#60a5fa' }}>{label}</p>
                <p className="text-sm font-semibold text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-12 py-10 space-y-14">

          {/* ══ 01 EXECUTIVE SUMMARY ════════════════════════════ */}
          <section>
            <SectionTitle number="01" title="Executive Summary" />
            <Blurb>
              A snapshot of hiremeja.com's overall search performance for the reporting period.
              Organic traffic is the most important channel — visits driven by unpaid Google results
              signal that your SEO investment is compounding.
            </Blurb>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <KpiHero
                label="Organic Sessions"
                value={fmt(organicSessions)}
                sub={`${organicPct}% of all traffic`}
                note={`Target: ${fmt(tgtOrganic)}`}
                accent
              />
              <KpiHero label="Total Sessions"    value={fmt(totalSessions)}   sub={`Last ${days} days`} />
              <KpiHero label="Domain Authority"  value={ourMoz.domain_authority ?? '—'} sub="Moz — out of 100" />
              <KpiHero label="Avg. SERP Position" value={avgPosition ? `#${avgPosition}` : '—'} sub="Top 10 keywords (GSC)" />
            </div>

            <div className="rounded-lg px-5 py-4 text-sm text-gray-600 leading-relaxed"
              style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
              Over the last <strong>{days} days</strong> ({periodLabel}), hiremeja.com recorded{' '}
              <strong>{fmt(totalSessions)}</strong> total sessions with{' '}
              <strong>{fmt(organicSessions)}</strong> ({organicPct}%) coming from organic Google
              search — the highest-value, zero-cost traffic channel. The site's Domain Authority
              of <strong>{ourMoz.domain_authority ?? '—'}/100</strong> reflects{' '}
              <strong>{fmt(ourMoz.linking_domains)}</strong> linking domains.
              The average ranking position for tracked keywords is{' '}
              <strong>{avgPosition ? `#${avgPosition}` : '—'}</strong>, with a realistic
              target of <strong>{tgtPosition ? `#${tgtPosition}` : '—'}</strong> over the next
              three months through targeted on-page optimisation.
            </div>
          </section>

          {/* ══ 02 KPI TARGETS ════════════════════════════════ */}
          <section>
            <SectionTitle number="02" title="KPI Targets — Current vs Goal" />
            <Blurb>
              Realistic targets set against current baselines. Progress bars show how close each
              metric is to its 3–6 month goal. All targets assume consistent content publishing
              and basic technical SEO maintenance.
            </Blurb>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Metric</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">Now</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 text-green-700">Target</th>
                    <th className="py-2 px-3 text-xs font-semibold text-gray-500">Progress</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500">Timeline</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">How to get there</th>
                  </tr>
                </thead>
                <tbody>
                  <KpiRow
                    label="Organic Sessions"
                    current={organicSessions}
                    target={tgtOrganic}
                    format={fmt}
                    timeline="3 months"
                    how="Publish 2 keyword-targeted blog posts per month"
                  />
                  <KpiRow
                    label="Total Sessions"
                    current={totalSessions}
                    target={tgtSessions}
                    format={fmt}
                    timeline="3 months"
                    how="Organic growth + social media sharing of content pages"
                  />
                  <KpiRow
                    label="Domain Authority"
                    current={ourMoz.domain_authority ?? 0}
                    target={tgtDA}
                    timeline="6 months"
                    how="Build 10 backlinks from Caribbean news & HR sites"
                  />
                  {avgPositionRaw && (
                    <KpiRow
                      label="Avg. SERP Position"
                      current={avgPosition}
                      target={tgtPosition}
                      format={v => `#${v}`}
                      lowerIsBetter
                      timeline="3 months"
                      how="Optimise title tags & H1s on top 10 landing pages"
                    />
                  )}
                  <KpiRow
                    label="Engagement Rate"
                    current={(engagementRate * 100).toFixed(1) + '%'}
                    target={(tgtEngagement * 100).toFixed(1) + '%'}
                    timeline="2 months"
                    how="Add related job links and CTAs to career resource pages"
                  />
                </tbody>
              </table>
            </div>

            <Insight color="#6366f1" bg="#f5f3ff">
              <strong>What moves the needle fastest:</strong> Ranking improvements follow a
              compounding curve — the jump from #15 → #8 drives 2–3× more clicks than the
              jump from #30 → #20. Focus optimisation effort on keywords already ranking
              positions 8–20 first (see Keyword Rankings section).
            </Insight>
          </section>

          {/* ══ 03 ORGANIC TRAFFIC ═════════════════════════════ */}
          <section className="page-break">
            <SectionTitle number="03" title="Organic Traffic" />
            <Blurb>
              Organic search sessions are visits from people who found hiremeja.com through an
              unpaid Google result. This is your most scalable channel — every piece of optimised
              content continues to attract traffic without ongoing ad spend.
            </Blurb>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <StatBox label="Organic Sessions"     value={fmt(organicSessions)} sub={`${organicPct}% of total`} />
              <StatBox label="Total Sessions"       value={fmt(totalSessions)} />
              <StatBox label="Active Users"         value={fmt(totalUsers)} sub={`${fmt(newUsers)} new users`} />
              <StatBox label="Page Views"           value={fmt(totalPageviews)} />
              <StatBox label="Engagement Rate"      value={pct(engagementRate)} />
              <StatBox label="Avg. Session Duration" value={fmtDuration(avgDuration)} />
            </div>

            {/* Sessions trend chart */}
            {tsData.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                  Sessions — {days}-day trend
                </p>
                <div className="flex items-end gap-px" style={{ height: 96 }}>
                  {tsData.map((d, i) => (
                    <div key={i} title={`${d.date}: ${d.sessions} sessions`}
                      className="flex-1 rounded-t transition-all"
                      style={{
                        height: `${Math.max(3, (d.sessions / maxSessions) * 96)}px`,
                        background: NAVY,
                        opacity: 0.8 + (i / tsData.length) * 0.2,
                      }}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                  <span>{tsData[0]?.date}</span>
                  <span>{tsData[Math.floor(tsData.length / 2)]?.date}</span>
                  <span>{tsData[tsData.length - 1]?.date}</span>
                </div>
              </div>
            )}

            {/* Traffic source segmented bar */}
            {channels?.length > 0 && (() => {
              const total = channels.reduce((s, r) => s + met(r, 0), 0) || 1;
              return (
                <div className="mb-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                    Traffic source split
                  </p>
                  <div className="flex rounded-lg overflow-hidden h-7" style={{ border: '1px solid #e5e7eb' }}>
                    {channels.map((r, i) => {
                      const ch  = dim(r, 0) || 'Unassigned';
                      const pct = (met(r, 0) / total) * 100;
                      return pct > 1 ? (
                        <div key={i} title={`${ch}: ${fmt(met(r, 0))} sessions`}
                          className="flex items-center justify-center overflow-hidden"
                          style={{ width: `${pct}%`, background: CHANNEL_COLORS[ch] || '#9ca3af' }}>
                          {pct > 8 && (
                            <span className="text-xs font-bold truncate px-1"
                              style={{ color: ch === 'Direct' ? NAVY : '#fff' }}>
                              {ch.replace(' Search', '').replace(' Social', '')}
                            </span>
                          )}
                        </div>
                      ) : null;
                    })}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {channels.map((r, i) => {
                      const ch = dim(r, 0) || 'Unassigned';
                      return (
                        <div key={i} className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ background: CHANNEL_COLORS[ch] || '#9ca3af' }} />
                          <span className="text-xs text-gray-500">{ch} — {fmt(met(r, 0))}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Geo + Device side by side */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                  Top countries
                </p>
                <div className="space-y-2.5">
                  {(countries || []).slice(0, 6).map((r, i) => {
                    const s   = met(r, 0);
                    const max = met(countries[0], 0) || 1;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600">{dim(r, 0)}</span>
                          <span className="font-semibold" style={{ color: NAVY }}>{fmt(s)}</span>
                        </div>
                        <Bar value={s} max={max} color={NAVY} />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                  Device breakdown
                </p>
                <div className="space-y-3">
                  {(devices || []).map((r, i) => {
                    const total = (devices || []).reduce((s, d) => s + met(d, 0), 0);
                    const s     = met(r, 0);
                    const share = total > 0 ? ((s / total) * 100).toFixed(1) : 0;
                    const clrs  = [NAVY, YELLOW, '#6366f1'];
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600 capitalize">{dim(r, 0)}</span>
                          <span className="font-semibold" style={{ color: NAVY }}>{share}%</span>
                        </div>
                        <Bar value={parseFloat(share)} max={100} color={clrs[i]} height={8} />
                      </div>
                    );
                  })}
                </div>
                {devices?.length > 0 && (
                  <p className="text-xs text-gray-400 mt-3">
                    Total: {fmt((devices || []).reduce((s, d) => s + met(d, 0), 0))} sessions
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* ══ 04 LLM VISIBILITY ═══════════════════════════════ */}
          <section className="page-break">
            <SectionTitle number="04" title="LLM & AI Visibility" />
            <Blurb>
              AI assistants (ChatGPT, Perplexity, Google AI Overviews, Claude) are increasingly
              the first point of contact for job seekers. A site that appears in AI-generated
              answers gets brand exposure before anyone even visits Google. This section scores
              hiremeja.com's readiness to be cited by AI models.
            </Blurb>

            {/* Score display */}
            <div className="flex items-center gap-6 mb-6 p-5 rounded-xl"
              style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
              <div className="text-center shrink-0">
                <p className="text-5xl font-black" style={{ color: llmColor }}>{llmScore}</p>
                <p className="text-xs font-bold uppercase tracking-wide mt-1" style={{ color: llmColor }}>
                  {llmLabel}
                </p>
                <p className="text-xs text-gray-400">out of 100</p>
              </div>
              <div className="flex-1">
                <div className="h-3 rounded-full" style={{ background: '#e5e7eb' }}>
                  <div className="h-3 rounded-full" style={{
                    width: `${llmScore}%`,
                    background: llmScore >= 80 ? '#16a34a' : llmScore >= 60 ? '#ca8a04' : '#dc2626',
                  }} />
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0 — Needs Work</span>
                  <span>40 — Moderate</span>
                  <span>60 — Good</span>
                  <span>80 — Strong</span>
                </div>
                <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                  hiremeja.com scores <strong style={{ color: llmColor }}>{llmScore}/100</strong> on
                  AI visibility readiness. The site already has the key structural signals in place.
                  The next gains come from raising Domain Authority and improving SERP rankings for
                  high-intent keywords.
                </p>
              </div>
            </div>

            {/* Checklist */}
            <div className="space-y-3">
              {llmChecks.map((c, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg"
                  style={{ background: c.done ? '#f0fdf4' : '#fef2f2', border: `1px solid ${c.done ? '#bbf7d0' : '#fecaca'}` }}>
                  <div className="shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-black"
                      style={{
                        background: c.done ? '#16a34a' : '#dc2626',
                        color: '#fff',
                      }}>
                      {c.done ? '✓' : '✗'}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold" style={{ color: c.done ? '#15803d' : '#b91c1c' }}>
                        {c.label}
                      </p>
                      <span className="text-xs font-bold ml-3 shrink-0"
                        style={{ color: c.done ? '#15803d' : '#9ca3af' }}>
                        +{c.pts} pts
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{c.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <Insight color="#7c3aed" bg="#f5f3ff">
              <strong>Next action:</strong> The biggest LLM visibility gain available right now is
              improving the average SERP position from{' '}
              <strong>#{avgPosition ?? '—'}</strong> to{' '}
              <strong>{tgtPosition ? `#${tgtPosition}` : 'under #10'}</strong>.
              AI models disproportionately cite pages that already rank on Google's first page.
              Adding JobPosting JSON-LD schema to all live job listings would also improve
              how AI assistants surface individual job opportunities.
            </Insight>
          </section>

          {/* ══ 05 KEYWORD RANKINGS ═════════════════════════════ */}
          <section className="page-break">
            <SectionTitle number="05" title="Keyword Rankings" />
            <Blurb>
              Google Search Console data showing which queries surface hiremeja.com in search
              results. Clicks are direct traffic. Impressions measure visibility even when
              people don't click. Position is the average ranking across all searches for that keyword.
            </Blurb>

            {/* Position distribution */}
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                Keyword position distribution — {(gscQueries || []).length} keywords tracked
              </p>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Top 3', sublabel: 'Positions 1–3', count: posBuckets.top3, color: '#16a34a', bg: '#dcfce7' },
                  { label: 'Page 1', sublabel: 'Positions 4–10', count: posBuckets.page1, color: '#ca8a04', bg: '#fef9c3' },
                  { label: 'Page 2', sublabel: 'Positions 11–20', count: posBuckets.page2, color: '#2563eb', bg: '#dbeafe' },
                  { label: 'Beyond', sublabel: 'Positions 21+', count: posBuckets.beyond, color: '#6b7280', bg: '#f3f4f6' },
                ].map(({ label, sublabel, count, color, bg }) => (
                  <div key={label} className="rounded-lg p-4 text-center" style={{ background: bg }}>
                    <p className="text-2xl font-black" style={{ color }}>{count}</p>
                    <p className="text-xs font-bold" style={{ color }}>{label}</p>
                    <p className="text-xs mt-0.5" style={{ color, opacity: 0.7 }}>{sublabel}</p>
                    <div className="mt-2 h-1 rounded-full" style={{ background: 'rgba(0,0,0,0.1)' }}>
                      <div className="h-1 rounded-full" style={{
                        width: `${Math.round((count / totalKws) * 100)}%`,
                        background: color,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <StatBox label="Total Impressions" value={fmt(totalImpressions)} sub="Last period" />
              <StatBox label="Total Clicks"      value={fmt(totalClicks)} />
              <StatBox label="Avg. Position"     value={avgPosition ? `#${avgPosition}` : '—'} sub="Top 10 keywords" />
            </div>

            {/* Top keywords table */}
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Top keywords by clicks
            </p>
            {topKeywords.length === 0 ? (
              <p className="text-sm text-gray-400">No keyword data available.</p>
            ) : (
              <table className="w-full text-sm mb-6">
                <thead>
                  <tr style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                    {['#', 'Keyword', 'Clicks', 'Impressions', 'CTR', 'Position'].map((h, i) => (
                      <th key={h} className={`py-2 px-3 text-xs font-semibold text-gray-500 ${i === 1 ? 'text-left' : i === 0 ? 'text-left w-8' : 'text-right'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topKeywords.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td className="py-2 px-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="py-2 px-3 text-gray-700">{row.keys?.[0]}</td>
                      <td className="py-2 px-3 text-right font-bold" style={{ color: NAVY }}>{fmt(row.clicks)}</td>
                      <td className="py-2 px-3 text-right text-gray-500">{fmt(row.impressions)}</td>
                      <td className="py-2 px-3 text-right text-gray-500">
                        {row.ctr != null ? `${(row.ctr * 100).toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-2 px-3 text-right"><PosBadge pos={row.position} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Opportunity keywords */}
            {opportunityKws.length > 0 && (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                  Quick-win opportunities — high impressions, low CTR, positions 4–25
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#fffbeb', borderTop: '1px solid #fde68a', borderBottom: '1px solid #fde68a' }}>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-yellow-700">Keyword</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-yellow-700">Impressions</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-yellow-700">CTR</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-yellow-700">Position</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-yellow-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opportunityKws.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #fef9c3' }}>
                        <td className="py-2 px-3 text-gray-700">{row.keys?.[0]}</td>
                        <td className="py-2 px-3 text-right font-semibold text-orange-600">{fmt(row.impressions)}</td>
                        <td className="py-2 px-3 text-right text-red-500">
                          {row.ctr != null ? `${(row.ctr * 100).toFixed(1)}%` : '—'}
                        </td>
                        <td className="py-2 px-3 text-right"><PosBadge pos={row.position} /></td>
                        <td className="py-2 px-3 text-xs text-gray-400">
                          Improve title tag & meta description
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Insight color="#ca8a04" bg="#fffbeb">
                  These keywords already show Google your pages are relevant — but searchers
                  aren't clicking. Rewriting the page title to be more specific and compelling
                  (e.g. adding "Jamaica 2025" or a number like "14 Parishes") can lift CTR
                  by 1–3%, translating directly to more organic visits at zero cost.
                </Insight>
              </>
            )}
          </section>

          {/* ══ 06 CONTENT PERFORMANCE ══════════════════════════ */}
          <section className="page-break">
            <SectionTitle number="06" title="Content Performance" />
            <Blurb>
              Top pages ranked by total views during the reporting period. Content pages
              (career resources, specialty job categories) build long-term organic authority
              while job listing pages drive conversion. Both matter.
            </Blurb>

            {!(pages?.length) ? (
              <p className="text-sm text-gray-400">No page data available.</p>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 w-8">#</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Page</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Views</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Users</th>
                      <th className="py-2 px-3 text-xs font-semibold text-gray-500">Share of traffic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.slice(0, 15).map((r, i) => {
                      const views    = met(r, 0);
                      const maxViews = met(pages[0], 0) || 1;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td className="py-2 px-3 text-gray-400 text-xs">{i + 1}</td>
                          <td className="py-2 px-3 text-gray-700 font-mono text-xs"
                            style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {dim(r, 0) || '/'}
                          </td>
                          <td className="py-2 px-3 text-right font-bold" style={{ color: NAVY }}>{fmt(views)}</td>
                          <td className="py-2 px-3 text-right text-gray-500">{fmt(met(r, 1))}</td>
                          <td className="py-2 px-3 w-32">
                            <Bar value={views} max={maxViews} height={5} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <Insight color="#1d4ed8" bg="#eff6ff">
                  <strong>Content priority:</strong> Pages ranking below #15 in the keyword table
                  but with high view counts are candidates for refresh — update the copy, add
                  internal links to related job categories, and ensure the page title includes
                  the year (2025). Google's freshness signal rewards recently-updated content.
                </Insight>
              </>
            )}
          </section>

          {/* ══ 07 AUTHORITY & COMPETITORS ══════════════════════ */}
          <section className="page-break">
            <SectionTitle number="07" title="Domain Authority & Competitor Comparison" />
            <Blurb>
              Domain Authority (DA) is Moz's 1–100 scale predicting how well a site ranks on
              Google. It grows slowly through quality backlinks. The table below benchmarks
              hiremeja.com against direct competitors in the Caribbean job market.
            </Blurb>

            {mozSorted.length === 0 ? (
              <p className="text-sm text-gray-400">No Moz data available.</p>
            ) : (
              <>
                {/* DA bar comparison */}
                <div className="mb-5 space-y-2.5">
                  {mozSorted.map(({ domain, domain_authority }) => {
                    const isOurs = domain === 'hiremeja.com';
                    return (
                      <div key={domain} className="flex items-center gap-3">
                        <span className="w-40 text-xs text-gray-600 shrink-0 truncate font-medium">
                          {isOurs ? <strong style={{ color: NAVY }}>{domain} ★</strong> : domain}
                        </span>
                        <div className="flex-1 h-6 rounded overflow-hidden" style={{ background: '#f3f4f6' }}>
                          <div className="h-6 rounded flex items-center pl-2.5"
                            style={{
                              width: `${Math.round(((domain_authority || 0) / maxDA) * 100)}%`,
                              background: isOurs ? NAVY : '#d1d5db',
                              minWidth: 32,
                            }}>
                            <span className="text-xs font-black"
                              style={{ color: isOurs ? YELLOW : '#6b7280' }}>
                              {domain_authority ?? '—'}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 w-8 shrink-0 text-right">
                          /{maxDA}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Full metrics table */}
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Domain', 'DA', 'PA', 'Linking Domains', 'Spam Score'].map((h, i) => (
                        <th key={h} className={`py-2 px-3 text-xs font-semibold text-gray-500 ${i === 0 ? 'text-left' : i < 3 ? 'text-center' : 'text-right'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mozSorted.map(({ domain, domain_authority, page_authority, linking_domains, spam_score }) => {
                      const isOurs = domain === 'hiremeja.com';
                      return (
                        <tr key={domain} style={{
                          borderBottom: '1px solid #f3f4f6',
                          background: isOurs ? '#eff6ff' : 'transparent',
                        }}>
                          <td className="py-2.5 px-3 font-medium">
                            {isOurs
                              ? <span className="font-bold" style={{ color: NAVY }}>{domain} ★</span>
                              : <span className="text-gray-700">{domain}</span>}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span className="text-base font-black"
                              style={{ color: isOurs ? '#7c3aed' : '#374151' }}>
                              {domain_authority ?? '—'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-center text-gray-600 font-medium">{page_authority ?? '—'}</td>
                          <td className="py-2.5 px-3 text-right text-gray-600">{fmt(linking_domains)}</td>
                          <td className="py-2.5 px-3 text-right">
                            <span style={{ color: (spam_score || 0) > 30 ? '#ef4444' : '#16a34a', fontWeight: 600 }}>
                              {spam_score ?? '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <Insight color="#16a34a" bg="#f0fdf4">
                  <strong>Backlink strategy:</strong> hiremeja.com's DA of{' '}
                  <strong>{ourMoz.domain_authority ?? '—'}</strong> with a target of{' '}
                  <strong>{tgtDA}</strong> requires roughly 8–12 new links from sites with
                  DA 30+. Priority targets: Jamaican news sites (The Gleaner, Observer),
                  Caribbean HR associations, university career pages, and government
                  employment portals (mot.gov.jm, lmisidc.org.jm).
                </Insight>
              </>
            )}
          </section>

          {/* ══ 08 SERP RANK TRACKING ═══════════════════════════ */}
          <section className="page-break">
            <SectionTitle number="08" title="SERP Visibility & Rank Tracking" />
            <Blurb>
              Keyword visibility and ranking positions sourced from Rank Tracker ({RANK_DATA.date}).
              Visibility % measures how often hiremeja.com appears across all tracked
              keywords in each search engine — a higher score means more consistent
              presence on results pages.
            </Blurb>

            {/* ── Visibility overview ─────────────────────────── */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { label: 'Overall Visibility', value: RANK_DATA.visibility.overall, color: NAVY },
                { label: 'Google Visibility',  value: RANK_DATA.visibility.google,  color: '#2563eb' },
                { label: 'Yahoo! Visibility',  value: RANK_DATA.visibility.yahoo,   color: '#7c3aed' },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-xl border p-5 text-center"
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <p className="text-5xl font-black mb-1" style={{ color }}>{value}%</p>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">{label}</p>
                  <div className="h-2 rounded-full" style={{ background: '#e5e7eb' }}>
                    <div className="h-2 rounded-full" style={{ width: `${value}%`, background: color }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{RANK_DATA.keywordsTracked} keywords tracked</p>
                </div>
              ))}
            </div>

            {/* ── Keyword rankings table ───────────────────────── */}
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Keyword positions — Google &amp; Yahoo! ({RANK_DATA.date})
            </p>
            <table className="w-full text-sm mb-3">
              <thead>
                <tr style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 w-8">#</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Keyword</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold" style={{ color: '#2563eb' }}>Google</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold" style={{ color: '#7c3aed' }}>Yahoo!</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Priority / Action</th>
                </tr>
              </thead>
              <tbody>
                {RANK_DATA.keywords.map((row, i) => {
                  const priorityMeta = {
                    high:   { label: 'High priority',   bg: '#fef2f2', text: '#b91c1c' },
                    medium: { label: 'Medium priority', bg: '#fffbeb', text: '#a16207' },
                    low:    { label: 'Low priority',    bg: '#f0fdf4', text: '#15803d' },
                  }[row.priority];
                  const action =
                    row.priority === 'high'
                      ? 'Target with dedicated landing page + content'
                      : row.priority === 'medium'
                      ? 'Optimise existing page title & meta'
                      : 'Maintain — low commercial intent';
                  const isRankingWell = (pos) => pos !== '>30' && parseInt(pos) <= 10;
                  const rowBg = (isRankingWell(row.google) || isRankingWell(row.yahoo))
                    ? '#f0fdf4' : 'transparent';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: rowBg }}>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="py-2.5 px-3 font-semibold text-gray-700">{row.kw}</td>
                      <td className="py-2.5 px-3 text-center">
                        <RankBadge pos={row.google} engine="google" />
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <RankBadge pos={row.yahoo} engine="yahoo" />
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold px-1.5 py-0.5 rounded"
                            style={{ background: priorityMeta.bg, color: priorityMeta.text }}>
                            {priorityMeta.label}
                          </span>
                          <span className="text-xs text-gray-400">{action}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Legend */}
            <div className="flex gap-4 mb-6 text-xs">
              {[
                { bg: '#dcfce7', text: '#15803d', label: '#1–3  Top 3' },
                { bg: '#fef9c3', text: '#a16207', label: '#4–10  Page 1' },
                { bg: '#fee2e2', text: '#b91c1c', label: '#11–30  Page 2' },
                { bg: '#f3f4f6', text: '#9ca3af', label: '>30  Not ranking' },
              ].map(({ bg, text, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 rounded font-semibold" style={{ background: bg, color: text }}>■</span>
                  <span className="text-gray-500">{label}</span>
                </div>
              ))}
            </div>

            <Insight color="#b91c1c" bg="#fef2f2">
              <strong>"Jamaica jobs", "job search", and "job portal"</strong> are the three
              highest-intent keywords — people searching these are ready to use a job board.
              hiremeja.com does not yet rank in the top 30 for any of them on Google. A
              dedicated, optimised landing page (e.g. <em>/jobs-in-jamaica</em>) targeting
              all three terms simultaneously is the single highest-leverage SEO action available.
            </Insight>

            {/* ── Competitor landscape ─────────────────────────── */}
            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
                Competitor SERP landscape — "jobs in Jamaica" search results
              </p>
              <p className="text-xs text-gray-400 mb-4">
                {COMPETITORS.length} domains identified competing for jobs-in-Jamaica keyword space.
                Organised by competitive threat level.
              </p>

              {/* Category breakdown bar */}
              {(() => {
                const counts = {};
                COMPETITORS.forEach(c => { counts[c.type] = (counts[c.type] || 0) + 1; });
                const types = Object.entries(counts).sort((a, b) => b[1] - a[1]);
                return (
                  <div className="mb-5">
                    <div className="flex rounded-lg overflow-hidden h-6" style={{ border: '1px solid #e5e7eb' }}>
                      {types.map(([type, count]) => {
                        const s = COMP_STYLE[type] || { bg: '#f3f4f6', text: '#6b7280' };
                        const w = Math.round((count / COMPETITORS.length) * 100);
                        return (
                          <div key={type} title={`${type}: ${count}`}
                            className="flex items-center justify-center overflow-hidden"
                            style={{ width: `${w}%`, background: s.bg, borderRight: `1px solid ${s.border}` }}>
                            {w > 8 && (
                              <span className="text-xs font-bold" style={{ color: s.text }}>
                                {count}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {types.map(([type, count]) => {
                        const s = COMP_STYLE[type] || { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' };
                        return (
                          <div key={type} className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm" style={{ background: s.bg, border: `1px solid ${s.border}` }} />
                            <span className="text-xs text-gray-500">{type} ({count})</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Competitor grid by category */}
              {['Major Platform', 'Caribbean Board', 'Local', 'Government', 'International', 'Social'].map(type => {
                const group = COMPETITORS.filter(c => c.type === type);
                if (!group.length) return null;
                const s = COMP_STYLE[type] || { bg: '#f9fafb', text: '#374151', border: '#e5e7eb' };
                const threatNote = {
                  'Major Platform':  'Broad audience, global reach — compete on local specificity',
                  'Caribbean Board': '⚠ Direct competitors — same target market as hiremeja.com',
                  'Local':           '⚠ Local competitors — often lower quality, beatable with SEO',
                  'Government':      'Official job listings only — not competing for employer subscriptions',
                  'International':   'Broad international boards — low relevance to Jamaican employers',
                  'Social':          'Community groups — informal, no subscription model',
                }[type];
                return (
                  <div key={type} className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-md"
                        style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}>
                        {type}
                      </span>
                      <span className="text-xs text-gray-400">{threatNote}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.map(({ name, domain }) => (
                        <div key={domain} className="rounded-md px-3 py-1.5 text-xs font-medium"
                          style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}>
                          {name}
                          <span className="ml-1.5 opacity-50 font-normal">{domain}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <Insight color="#15803d" bg="#f0fdf4">
              <strong>The real opportunity:</strong> Major platforms (LinkedIn, Indeed, Glassdoor)
              dominate globally but serve a general audience. Local competitors (BigUpJA,
              Jobs Jamaica, Jamaica Classified) have weak SEO and low domain authority.
              hiremeja.com's best path to page-1 rankings is to out-rank <em>local</em>{' '}
              competitors first, then close the gap on Caribbean boards — not to compete
              head-to-head with global platforms.
            </Insight>
          </section>

          {/* ── Footer ───────────────────────────────────────── */}
          <div className="pt-6 flex justify-between items-center text-xs text-gray-400"
            style={{ borderTop: '1px solid #e5e7eb' }}>
            <span>hiremeja.com — SEO Performance Report · {periodLabel}</span>
            <span>Generated {reportDate}</span>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default SeoReport;
